import { useEffect, useRef, useState } from 'react'

import { Box, Button, Paper, TextField, Typography, makeStyles } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { add, isAfter } from 'date-fns'

import Title from 'components/Title'

import { ChallengeData } from './SignInForm.types'

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
  },
  codeInputContainer: {
    marginTop: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'center',
  },
  digitInput: {
    width: 48,
    '& input': {
      textAlign: 'center',
      fontSize: 24,
      fontWeight: 500,
      padding: theme.spacing(1.5),
    },
  },
  errorBox: {
    paddingTop: theme.spacing(2),
    textAlign: 'center',
  },
  instructions: {
    textAlign: 'left',
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  helpText: {
    textAlign: 'center',
    marginTop: theme.spacing(2),
  },
}))

type TwoFactorFormProps = {
  challengeData: ChallengeData
  onSubmit: (code: string) => Promise<void>
  onCancel: (expired?: boolean) => void
  onResendCode: () => Promise<void>
}

const TwoFactorForm = ({ onSubmit, onCancel, onResendCode }: TwoFactorFormProps) => {
  const classes = useStyles()
  const [timeUntilExpiry] = useState(add(new Date(), { minutes: 10 }))
  const [isExpired, setIsExpired] = useState(false)
  const [saving, setSaving] = useState(false)
  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', ''])
  const [errorMessage, setErrorMessage] = useState('')
  const [resendCooldown, setResendCooldown] = useState(0) // Start at 0 (enabled)
  const [isResending, setIsResending] = useState(false)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  const code = digits.join('')
  const isValid = code.length === 6

  useEffect(() => {
    // Focus first input on mount
    inputRefs.current[0]?.focus()
  }, [])

  useEffect(() => {
    // Check expiration every 5 seconds
    const interval = setInterval(() => {
      if (isAfter(new Date(), timeUntilExpiry)) {
        setIsExpired(true)
      }
    }, 5000)

    return () => clearInterval(interval)
  }, [timeUntilExpiry])

  useEffect(() => {
    // Auto-redirect when code expires
    if (isExpired) {
      onCancel(true)
    }
  }, [isExpired, onCancel])

  useEffect(() => {
    // Countdown timer for resend button
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCooldown])

  const handleChange = (index: number, value: string) => {
    // Only allow single digit
    if (value.length > 1) {
      value = value.slice(-1)
    }

    // Only allow numbers
    if (value && !/^\d$/.test(value)) {
      return
    }

    const newDigits = [...digits]
    newDigits[index] = value
    setDigits(newDigits)

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      // Focus previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus()
    } else if (e.key === 'Enter' && isValid) {
      handleSubmit()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').trim()

    // Only handle 6-digit numeric paste
    if (/^\d{6}$/.test(pastedData)) {
      const newDigits = pastedData.split('')
      setDigits(newDigits)
      // Focus last input after paste
      inputRefs.current[5]?.focus()
    }
  }

  const handleSubmit = async () => {
    if (!isValid) return

    setSaving(true)
    setErrorMessage('')

    try {
      await onSubmit(code)
    } catch (error) {
      setErrorMessage('Invalid passcode. Please try again.')
      setSaving(false)
    }
  }

  const handleResendCode = async () => {
    setIsResending(true)
    setErrorMessage('')
    try {
      await onResendCode()
      setResendCooldown(30) // 30s cooldown after clicking
      setDigits(['', '', '', '', '', '']) // Clear the inputs
      inputRefs.current[0]?.focus() // Focus first input
    } catch (error) {
      setErrorMessage('Failed to resend code. Log in again to get a new code.')
    } finally {
      setIsResending(false)
    }
  }

  return (
    <Paper
      elevation={0}
      className={classes.container}
    >
      {(errorMessage || isExpired) && (
        <Box
          mb={2}
          className={classes.errorBox}
        >
          <Alert severity="error">
            {isExpired ? 'Your code has expired. Log in again to get a new code.' : errorMessage}
          </Alert>
        </Box>
      )}

      <Title title="Verify Your Identity" />
      <Typography
        variant="body1"
        className={classes.instructions}
        id="code-input-instructions"
      >
        We've sent a 6-digit code to your email address. Enter the code below to gain access to Scoir.
      </Typography>

      <Box
        className={classes.codeInputContainer}
        role="group"
        aria-labelledby="code-input-instructions"
      >
        {digits.map((digit, index) => (
          <TextField
            key={index}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={index === 0 ? handlePaste : undefined}
            variant="outlined"
            className={classes.digitInput}
            inputProps={{
              maxLength: 1,
              inputMode: 'numeric',
              pattern: '[0-9]*',
              'aria-label': `Digit ${index + 1}`,
            }}
            inputRef={(el) => (inputRefs.current[index] = el)}
            autoComplete="off"
            error={!!errorMessage}
          />
        ))}
      </Box>

      <Box className={classes.buttonContainer}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={isExpired || saving || !isValid}
        >
          Confirm
        </Button>

        <Button
          color="primary"
          variant="outlined"
          onClick={handleResendCode}
          disabled={resendCooldown > 0 || isResending}
        >
          {resendCooldown > 0 ? `Resend Code (${resendCooldown}s)` : 'Resend Code'}
        </Button>

        <Button
          color="primary"
          variant="text"
          onClick={() => onCancel(false)}
          disabled={saving}
        >
          Back to Login
        </Button>

        <Button
          color="primary"
          variant="text"
          component="a"
          href="https://www.scoir.com/support"
          target="_blank"
          rel="noopener noreferrer"
        >
          Contact Us
        </Button>
      </Box>
    </Paper>
  )
}

export default TwoFactorForm
